import type { GroupPrivate } from '@repo/types/groups';
import type { UserPublic } from '@repo/types/users';

import { useCreateChat } from '@repo/api/chat/useCreateChat';
import { useGetCreateConversationUsersInfinite } from '@repo/api/chat/useGetCreateConversationUsersInfinite';
import { useGetAssignedGroupsInfinite } from '@repo/api/group/useGetAssignedGroupsInfinite';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { getUserLvl } from '@repo/lib/user-lvl/getUserLvl';
import { CheckCircle2Icon, LoaderCircleIcon, UserIcon, UsersIcon } from 'lucide-react';
import { useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { SearchInput } from '@/components/ui/input';
import { LoadingSwap } from '@/components/ui/loading-swap';
import {
  SelectList,
  SelectListElement,
  SelectListElementContent,
  SelectListElementDescription,
  SelectListElementSkeleton,
  SelectListElementTitle,
  SelectListElementTrigger,
  SelectListInfiniteQueryStates,
} from '@/components/ui/select-list';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tag } from '@/components/ui/tags';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { useCredentials } from '@/store/auth-store';
import { getAvatar } from '@/utils/getAvatar';

/**
 * Props for the CreateChat component.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface CreateChatProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function CreateChat(createChatProps: CreateChatProps) {
  const { open, onOpenChange } = createChatProps;
  const { lvl } = useCredentials();
  const teacherLvl: boolean = lvl >= 1;
  const [searchValue, setSearchValue] = useState<string>('');
  const searchDebouncedValue = useDebounceValue(searchValue, 400);

  const [selectedUsers, setSelectedUsers] = useState<UserPublic[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<GroupPrivate[]>([]);

  const { errorMessage: usersErrorMessage, ...getUsersInfinite } = useGetCreateConversationUsersInfinite({
    name: searchDebouncedValue,
  });

  const { data: users, isEmpty: usersIsEmpty, noResults: usersNoResults } = useInfiniteQueryResult(getUsersInfinite.data, searchDebouncedValue);

  const { errorMessage: groupErrorMessage, ...getAssignedGroupsInfinite } = useGetAssignedGroupsInfinite(searchDebouncedValue);

  const { data: groups, isEmpty: groupsIsEmpty, noResults: groupsNoResults } = useInfiniteQueryResult(getAssignedGroupsInfinite.data, searchDebouncedValue);

  const { mutateAsync, isPending } = useCreateChat();

  const onClose = () => {
    onOpenChange(false);
    setSelectedUsers([]);
    setSelectedGroups([]);
  };
  const onSubmit = async () => {
    const userIds = selectedUsers.map((u) => u.id);
    const groupIds = selectedGroups.map((g) => g.id);
    await mutateAsync({ userIds: userIds, groupIds: groupIds })
      .then(() => {
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };
  const onRemoveUser = (id: number) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== id));
  };
  const onRemoveGroup = (id: number) => {
    setSelectedGroups((prev) => prev.filter((g) => g.id !== id));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'sm:h-[85dvh] sm:min-h-96 sm:max-w-108'} onCloseAutoFocus={onClose} onEscapeKeyDown={onClose}>
        {isPending && (
          <div className={'bg-background/75 absolute inset-0 z-60 m-auto flex flex-wrap items-center justify-center gap-3 backdrop-blur-md'}>
            <LoaderCircleIcon className={'animate-spin'} aria-hidden={'true'} />
            <h5 className={'font-medium'}>Trwa tworzenie chatu...</h5>
          </div>
        )}

        <DialogBody>
          <DialogHeader className={'border-border-primary border-b py-5'}>
            <DialogTitle>Utwórz konwersację</DialogTitle>
          </DialogHeader>
          <DialogDescription className={'sr-only'}>Formularz do tworzenia chatu</DialogDescription>
          <Tabs className={'flex grow flex-col gap-3 overflow-y-auto p-4'} defaultValue={'users'}>
            <div className={'flex flex-col gap-4'}>
              {teacherLvl && (
                <TabsList className={'grid w-full grid-cols-2'}>
                  <TabsTrigger value={'users'}>Użytkownicy</TabsTrigger>
                  <TabsTrigger value={'groups'}>Grupy</TabsTrigger>
                </TabsList>
              )}

              <SearchInput setValue={setSearchValue} placeholder="Wyszukaj..." />
              {teacherLvl && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedUsers.map(({ id, name, surname, avatarURL }) => (
                    <Tag key={id} onRemove={() => onRemoveUser(id)} variant={'muted'} border>
                      <Avatar size={'xs'}>
                        <AvatarImage src={getAvatar(avatarURL)} alt={name + '_' + surname} />
                        <AvatarFallback>
                          <UserIcon />
                        </AvatarFallback>
                      </Avatar>
                      {name} {surname.at(0)}.
                    </Tag>
                  ))}
                  {selectedGroups.map(({ id, name, avatarURL }) => (
                    <Tag key={id} onRemove={() => onRemoveGroup(id)} variant={'muted'} border>
                      <Avatar size={'xs'}>
                        <AvatarImage src={getAvatar(avatarURL)} alt={name} />
                        <AvatarFallback>
                          <UsersIcon />
                        </AvatarFallback>
                      </Avatar>
                      {name}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
            <TabsContent value={'users'}>
              <SelectList
                type={teacherLvl ? 'multiple' : 'single'}
                selected={selectedUsers.map((u) => String(u.id))}
                setSelected={(ids) => {
                  const selectedIds = new Set(ids.map(Number));
                  const updatedUsers = [...selectedUsers.filter((u) => selectedIds.has(u.id)), ...users.filter((u) => selectedIds.has(u.id) && !selectedUsers.some((s) => s.id === u.id))];
                  setSelectedUsers(updatedUsers);
                }}
              >
                <SelectListInfiniteQueryStates
                  query={getUsersInfinite}
                  isEmpty={usersIsEmpty}
                  noResults={usersNoResults}
                  isEmptyMessage={'Zmień parametry wyszukiwania'}
                  noResultMessage={'Brak użytkowników'}
                  errorMessage={usersErrorMessage}
                />

                {users.map((user) => {
                  return (
                    <SelectListElement key={user.id} id={String(user.id)}>
                      <Avatar size={'sm'}>
                        <AvatarImage src={getAvatar(user.avatarURL)} alt={user.name} />
                        <AvatarFallback>{user.name[0] ?? <UserIcon />}</AvatarFallback>
                      </Avatar>
                      <SelectListElementContent>
                        <SelectListElementTitle>
                          {user.name} {user.surname}
                        </SelectListElementTitle>
                        <SelectListElementDescription>{getUserLvl(user.lvl, user.accountType)}</SelectListElementDescription>
                      </SelectListElementContent>

                      <SelectListElementTrigger>
                        {({ selected, toggleElement }) => (
                          <Checkbox
                            checked={selected}
                            onCheckedChange={() => {
                              toggleElement();
                            }}
                            className="after:absolute after:inset-0"
                          />
                        )}
                      </SelectListElementTrigger>
                    </SelectListElement>
                  );
                })}
                <InfiniteScroll
                  hasNextPage={getUsersInfinite.hasNextPage}
                  isLoading={getUsersInfinite.isLoading}
                  isFetching={getUsersInfinite.isFetching}
                  next={getUsersInfinite.fetchNextPage}
                  threshold={0.65}
                >
                  {getUsersInfinite.hasNextPage && <SelectListElementSkeleton />}
                </InfiniteScroll>
              </SelectList>
            </TabsContent>
            {teacherLvl && (
              <TabsContent value={'groups'}>
                <SelectList
                  type={teacherLvl ? 'multiple' : 'single'}
                  selected={selectedGroups.map((g) => String(g.id))}
                  setSelected={(ids) => {
                    const selectedIds = new Set(ids.map(Number));
                    const updatedGroups = [...selectedGroups.filter((g) => selectedIds.has(g.id)), ...groups.filter((g) => selectedIds.has(g.id) && !selectedGroups.some((s) => s.id === g.id))];
                    setSelectedGroups(updatedGroups);
                  }}
                >
                  <SelectListInfiniteQueryStates
                    query={getAssignedGroupsInfinite}
                    isEmpty={groupsIsEmpty}
                    noResults={groupsNoResults}
                    isEmptyMessage={'Zmień parametry wyszukiwania'}
                    noResultMessage={'Brak grup'}
                    errorMessage={groupErrorMessage}
                  />

                  {groups.map((group) => {
                    return (
                      <SelectListElement key={group.id} id={String(group.id)}>
                        <Avatar size={'sm'}>
                          <AvatarImage src={getAvatar(group.avatarURL)} alt={group.name} />
                          <AvatarFallback>{group.name[0] ?? <UsersIcon />}</AvatarFallback>
                        </Avatar>
                        <SelectListElementContent>
                          <SelectListElementTitle>{group.name}</SelectListElementTitle>
                          <SelectListElementDescription>{group.users.length} użytkowników</SelectListElementDescription>
                        </SelectListElementContent>

                        <SelectListElementTrigger>
                          {({ selected, toggleElement }) => (
                            <Checkbox
                              checked={selected}
                              onCheckedChange={() => {
                                toggleElement();
                              }}
                              className="after:absolute after:inset-0"
                            />
                          )}
                        </SelectListElementTrigger>
                      </SelectListElement>
                    );
                  })}
                  <InfiniteScroll
                    hasNextPage={getAssignedGroupsInfinite.hasNextPage}
                    isLoading={getAssignedGroupsInfinite.isLoading}
                    isFetching={getAssignedGroupsInfinite.isFetching}
                    next={getAssignedGroupsInfinite.fetchNextPage}
                    threshold={0.65}
                  >
                    {getAssignedGroupsInfinite.hasNextPage && <SelectListElementSkeleton />}
                  </InfiniteScroll>
                </SelectList>
              </TabsContent>
            )}
          </Tabs>
        </DialogBody>
        <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
            Anuluj
          </Button>
          <Button disabled={(selectedUsers.length === 0 && selectedGroups.length === 0) || isPending} onClick={onSubmit} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
            <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
              <CheckCircle2Icon />
              Zapisz
            </LoadingSwap>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
